import { mdiCheck, mdiClose, mdiPencil, mdiDelete, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import { bomItemDefaultValue, BOMItemModel } from "../../models/npd_model";
import { ItemModelV2 } from "../../models/item_model";
import POItemSearchFieldV2 from "../../../purchase_order/purchase_order_create/components/po-item-search-widget";
import { numberRegEx } from "../../../../utils/constant";
import WarehouseSearchField from "../../warehouse/warehouse_search_field";
import { generate_id } from "../../journals/destination_stock_voucher/components/item_row_status";

interface BOMItemComponentProps {
  data?: BOMItemModel;
  onSubmit: (data: BOMItemModel) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: BOMItemModel) => void;
  existingItems?: BOMItemModel[];
}

const BOMReturnedItemComponent: FunctionComponent<BOMItemComponentProps> = (
  props,
) => {
  const [data, setData] = useState<BOMItemModel>({
    ...bomItemDefaultValue,
  });

  const { showToast } = useToast();
  const { edit, onSubmit, existingItems } = props;
  const [editable, setEditable] = useState(false);

  useEffect(() => {
    setEditable(edit);
  }, []);

  useEffect(() => {
    if (props.data) {
      setData({ ...props.data });
    }
  }, [props.data]);

  const onDone = () => {
    if (!data.item || data.qty < 0 || !data.warehouse?.name) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, Qty and Warehouse",
      });
      return;
    }
    onSubmit(data);
  };

  const handleItemSelect = async (selectedItem: ItemModelV2) => {
    setData((prevData) => ({
      ...prevData,
      id: generate_id(),
      item: selectedItem,
      item_id: selectedItem.id,
    }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onDone();
      }}
      className="bg-white text-xs h-full"
    >
      <div className="border rounded-lg shadow-sm p-3 text-xs flex flex-col gap-2 bg-white h-full">
        {/* Item */}
        <div>
          <label className="text-gray-400">Item</label>
          <POItemSearchFieldV2
            disabled={!editable || props.disabled}
            value={data.item}
            onSelect={handleItemSelect}
          />
        </div>

        {/* Warehouse */}
        <div>
          <label className="text-gray-400">Warehouse</label>
          <WarehouseSearchField
            type={""}
            value={data.warehouse}
            placeholder=""
            onSelect={(d) => {
              setData((old) => {
                return {
                  ...old,
                  warehouse: d,
                };
              });
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <div>
            <span className="text-gray-400">Item Code</span>
            <div className="font-semibold break-words">
              {data?.item?.item_code ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Unit Measure</span>
            <div className="font-semibold break-words">
              {data?.item?.unit?.symbol ?? "--"}
            </div>
          </div>
          {/* Quantity */}
          <div>
            <span className="text-gray-400">Quantity</span>
            <input
              value={data.qty ?? 0}
              disabled={!editable}
              placeholder=""
              type="number"
              className="w-full p-1 focus:outline-none border rounded"
              onChange={(e) => {
                const val = e.target.value.trim();
                if (val === "" || !numberRegEx.test(e.target.value)) return;

                setData((prevData) => ({
                  ...prevData,
                  qty: Number(val),
                }));
              }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 items-center justify-end border-t pt-2 mt-auto">
          {props.data ? (
            !props.disabled && (
              <>
                {editable ? (
                  <>
                    <div
                      onClick={() => {
                        if (!window.confirm("Are you sure !")) return;
                        onDone();
                        setEditable(false);
                      }}
                      className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200 cursor-pointer"
                    >
                      <Icon path={mdiCheck} className="h-4 w-4"></Icon>
                    </div>
                    <div
                      onClick={() => {
                        setData(props.data!);
                        setEditable(false);
                      }}
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer"
                    >
                      <Icon path={mdiClose} className="h-4 w-4"></Icon>
                    </div>
                  </>
                ) : (
                  <div
                    onClick={() => setEditable(true)}
                    className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200 cursor-pointer"
                  >
                    <Icon path={mdiPencil} className="h-4 w-4"></Icon>
                  </div>
                )}
                <div
                  onClick={() => {
                    if (!window.confirm("Are you sure !")) return;
                    if (props.onDelete) props.onDelete(data);
                  }}
                  className="bg-red-200 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer"
                >
                  <Icon path={mdiDelete} className="h-4 w-4"></Icon>
                </div>
              </>
            )
          ) : (
            <button className="flex gap-1 text-white text-xs bg-myPrimaryColor rounded-full px-3 py-1">
              <Icon path={mdiPlus} className="h-4 w-4"></Icon> Add
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default BOMReturnedItemComponent;
